import React, { useState, useCallback, useEffect } from "react";
import { BaseModal } from "../ui/BaseModal.tsx";
import { CustomToggleTabs } from "../ui/CustomToggleTabs.tsx";
import { CustomAlert } from "../ui/CustomAlert.tsx";
import { Box, Button, Typography, styled } from "@mui/material";
import { List, Grid, Search, X, Info } from "lucide-react";
import { useTheme } from "../ThemeContext";
import { useFileManagement } from "../../hooks/useFileManagement.ts";
import { useFileOperations } from "../../hooks/useFileOperations.ts";
import { FileNavigation } from "./fileManagement/FileNavigation.tsx";
import FileExplorer from "./fileManagement/FileExplorer.tsx";
import FolderCreator from "./fileManagement/FolderCreator.tsx";
import CollectionCreator from "./fileManagement/CollectionCreator.tsx";
import FileUploadSection from "./fileManagement/FileUploadSection.tsx";
import Pagination from "../ui/Pagination.tsx";

interface Collection {
  files?: any[];
  [key: string]: any;
}

interface FileManagementModalProps {
  onClose: () => void;
  isOpen: boolean;
}

const InfoBox = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  display: "flex",
  alignItems: "flex-start",
  padding: "8px",
  backgroundColor: $isDark ? "rgba(34, 197, 94, 0.1)" : "rgba(34, 197, 94, 0.05)",
  borderRadius: "8px",
  marginBottom: "24px",
  border: `1px solid #22C55E`,
  "& .info-icon": {
    color: $isDark ? "#22C55E" : "#16A34A",
    marginRight: "8px",
  },
  "& .info-text": {
    color: $isDark ? "#D1FAE5" : "#065F46",
    fontSize: "13px",
    "@media (max-width: 600px)": {
      fontSize: "12px",
    },
  },
}));

export default function FileManagementModal({
  onClose,
  isOpen,
}: FileManagementModalProps) {
  const { isDarkMode } = useTheme();
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [internalTextConfig, setInternalTextConfig] = useState<any>({});

  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    type: "loading" | "success" | "error" | "confirm";
    message: string;
    title?: string;
    onConfirm?: () => Promise<void>;
  }>({
    open: false,
    type: "loading",
    message: "",
    title: "",
    onConfirm: undefined,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const fetchInternalTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setInternalTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchInternalTextConfig();
  }, []);

  const {
    collections,
    setCollections,
    currentPath,
    isLoading,
    isRefreshing,
    defaultCollection,
    textConfig: hookTextConfig,
    fetchUserDocuments,
    handleManualRefresh,
    handleFolderClick,
    handleNavigateBack,
    handleNavigateToRoot,
    handleNavigateToPath,
    truncateFileName,
    pagination,
    handlePageChange,
    handleSearchByFile,
    handleClearSearch,
    handleDownloadFile,
  } = useFileManagement(isOpen);

  const textConfig = hookTextConfig || internalTextConfig;

  const {
    selectedFiles,
    setSelectedFiles,
    newCollectionFiles,
    setNewCollectionFiles,
    newCollectionName,
    setNewCollectionName,
    isUploading,
    isCreatingCollection,
    fileNameError,
    setFileNameError,
    collectionNameError,
    setCollectionNameError,
    showInlineSuccessMessage,
    setShowInlineSuccessMessage,
    showFileUploadSuccessMessage,
    setShowFileUploadSuccessMessage,
    processFileSelection,
    handleUploadFiles,
    handleCreateCollection,
    handleCreateFolder,
    handleDeleteFile,
  } = useFileOperations(
    collections,
    setCollections,
    currentPath,
    defaultCollection,
    textConfig,
    fetchUserDocuments
  );

  const handleClose = () => {
    setShowFileUploadSuccessMessage(false);
    onClose();
  };

  const handleCloseAlert = useCallback(() => {
    setAlertConfig((prev) => ({ ...prev, open: false }));
  }, []);



  // const handleDeleteFolder = (folderName: string) => {
  //   const getFilesInFolder = (
  //     currentCollection: any,
  //     path: string[]
  //   ): { hash: string; collection: string }[] => {
  //     const result: { hash: string; collection: string }[] = [];

  //     if (path.length === 0) {
  //       if (currentCollection.files && Array.isArray(currentCollection.files)) {
  //         currentCollection.files.forEach((file: any) => {
  //           if (file.FILE_NAME && file.COLLECTION_NAME) {
  //             result.push({
  //               hash: file.FILE_NAME,
  //               collection: file.COLLECTION_NAME,
  //             });
  //           }
  //         });
  //       }

  //       Object.keys(currentCollection).forEach((key) => {
  //         if (key !== "files" && typeof currentCollection[key] === "object") {
  //           result.push(...getFilesInFolder(currentCollection[key], []));
  //         }
  //       });

  //       return result;
  //     }

  //     const nextFolder = path[0];
  //     if (currentCollection[nextFolder]) {
  //       return getFilesInFolder(currentCollection[nextFolder], path.slice(1));
  //     }

  //     return [];
  //   };

  //   let targetCollection: Collection;
  //   if (currentPath.length === 0) {
  //     targetCollection = collections[folderName];
  //   } else {
  //     let current = collections;
  //     for (const folder of currentPath) {
  //       current = current[folder];
  //     }
  //     targetCollection = current[folderName];
  //   }

  //   const filesToDelete = getFilesInFolder(targetCollection, []);
  //   const isCollection = currentPath.length === 0;
  //   const itemType = isCollection ? textConfig.FILE_MANAGEMENT_MODAL_COLLECTION : textConfig.FILE_MANAGEMENT_MODAL_FOLDER;

  //   if (!isCollection && filesToDelete.length === 1) {
  //     setAlertConfig({
  //       open: true,
  //       type: "error",
  //       message: "No se puede eliminar el último archivo de una carpeta. Una carpeta nunca puede quedar vacía.",
  //       title: "Error al eliminar",
  //     });
  //     return;
  //   }

  //   const confirmMessage = filesToDelete.length > 0
  //     ? textConfig.FILE_MANAGEMENT_MODAL_DELETE_WITH_FILES_CONFIRM
  //         ?.replace('{itemType}', itemType.toLowerCase())
  //         ?.replace('{fileCount}', filesToDelete.length.toString())
  //         ?.replace('{filesText}', filesToDelete.length === 1 ? textConfig.FILE_MANAGEMENT_MODAL_FILE_SINGULAR : textConfig.FILE_MANAGEMENT_MODAL_FILE_PLURAL)
  //         ?.replace('{foundText}', filesToDelete.length === 1 ? textConfig.FILE_MANAGEMENT_MODAL_FOUND_SINGULAR : textConfig.FILE_MANAGEMENT_MODAL_FOUND_PLURAL)
  //     : textConfig.FILE_MANAGEMENT_MODAL_DELETE_CONFIRM
  //         ?.replace('{itemType}', itemType.toLowerCase());

  //   const titleText = textConfig.FILE_MANAGEMENT_MODAL_DELETE_TITLE?.replace('{itemType}', itemType);

  //   setAlertConfig({
  //     open: true,
  //     type: "confirm",
  //     title: titleText,
  //     message: confirmMessage,
  //     onConfirm: async () => {
  //       try {
  //         setAlertConfig({
  //           open: true,
  //           type: "loading",
  //           message: textConfig.FILE_MANAGEMENT_MODAL_DELETING,
  //           title: textConfig.FILE_MANAGEMENT_MODAL_PROCESSING,
  //         });

  //         let errors = 0;

  //         if (isCollection) {
  //           try {
  //             await deleteCollection(folderName);
  //           } catch (error) {
  //             console.error(`Error al eliminar colección ${folderName}:`, error);
  //             errors = 1;
  //           }
  //         } else {
  //           for (const file of filesToDelete) {
  //             try {
  //               await deleteFile(file.hash);
  //             } catch (error) {
  //               console.error(textConfig.FILE_MANAGEMENT_MODAL_ERROR_DELETING_FILE?.replace('{fileHash}', file.hash) || `Error al eliminar archivo ${file.hash}:`, error);
  //               errors++;
  //             }
  //           }
  //         }

  //         const updatedCollections = { ...collections };
  //         if (currentPath.length === 0) {
  //           delete updatedCollections[folderName];
  //         } else {
  //           let current = updatedCollections;
  //           for (let i = 0; i < currentPath.length; i++) {
  //             current = current[currentPath[i]];
  //           }
  //           delete current[folderName];
  //         }

  //         setCollections(updatedCollections);

  //         let successMessage = "";
  //         if (filesToDelete.length > 0) {
  //           if (errors > 0) {
  //             successMessage = textConfig.FILE_MANAGEMENT_MODAL_DELETE_PARTIAL_SUCCESS
  //               ?.replace('{itemType}', itemType)
  //               ?.replace('{successCount}', (filesToDelete.length - errors).toString())
  //               ?.replace('{totalCount}', filesToDelete.length.toString())
  //               ?.replace('{errorCount}', errors.toString())
  //               ?.replace('{errorFilesText}', errors === 1 ? textConfig.FILE_MANAGEMENT_MODAL_FILE_SINGULAR : textConfig.FILE_MANAGEMENT_MODAL_FILE_PLURAL)
  //               ?.replace('{couldNotText}', errors === 1 ? textConfig.FILE_MANAGEMENT_MODAL_COULD_NOT_SINGULAR : textConfig.FILE_MANAGEMENT_MODAL_COULD_NOT_PLURAL);
  //           } else {
  //             successMessage = textConfig.FILE_MANAGEMENT_MODAL_DELETE_SUCCESS_WITH_FILES
  //               ?.replace('{itemType}', itemType)
  //               ?.replace('{fileCount}', filesToDelete.length.toString())
  //               ?.replace('{filesText}', filesToDelete.length === 1 ? textConfig.FILE_MANAGEMENT_MODAL_FILE_SINGULAR : textConfig.FILE_MANAGEMENT_MODAL_FILE_PLURAL)
  //               ?.replace('{deletedText}', filesToDelete.length === 1 ? textConfig.FILE_MANAGEMENT_MODAL_DELETED_SINGULAR : textConfig.FILE_MANAGEMENT_MODAL_DELETED_PLURAL);
  //           }
  //         } else {
  //           successMessage = textConfig.FILE_MANAGEMENT_MODAL_DELETE_SUCCESS
  //             ?.replace('{itemType}', itemType);
  //         }

  //         setAlertConfig({
  //           open: true,
  //           type: "success",
  //           message: successMessage,
  //           onConfirm: async () => {
  //             setAlertConfig((prev) => ({ ...prev, open: false }));
  //             await fetchUserDocuments();
  //           },
  //         });
  //       } catch (error: any) {
  //         console.error(textConfig.FILE_MANAGEMENT_MODAL_ERROR_IN_DELETE || "Error en handleDeleteFolder:", error);
  //         setAlertConfig({
  //           open: true,
  //           type: "error",
  //           message: textConfig.FILE_MANAGEMENT_MODAL_DELETE_ERROR?.replace('{itemType}', itemType.toLowerCase()),
  //           onConfirm: async () => {
  //             setAlertConfig((prev) => ({ ...prev, open: false }));
  //             await fetchUserDocuments();
  //           },
  //         });
  //       }
  //     },
  //   });
  // };

  useEffect(() => {
    setSelectedFiles([]);
  }, [currentPath, setSelectedFiles]);

  useEffect(() => {
    setShowFileUploadSuccessMessage(false);
  }, [currentPath, setShowFileUploadSuccessMessage]);

  const handleSearchSubmit = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      await handleSearchByFile(searchQuery.trim(), setAlertConfig);
    } catch (error) {
      console.error("Error searching files:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearchSubmit = async () => {
    setSearchQuery("");
    setIsSearching(true);
    try {
      await handleClearSearch(setAlertConfig);
    } catch (error) {
      console.error("Error clearing search:", error);
    } finally {
      setIsSearching(false);
    }
  };



  return (
    <>
      <BaseModal
        title={textConfig.FILE_MANAGEMENT_MODAL_TITLE}
        onClose={handleClose}
        isOpen={isOpen}
      >
        <CustomToggleTabs
          value={viewMode}
          onChange={(newValue) => setViewMode(newValue as "list" | "grid")}
          options={[
            {
              value: "list",
              label: textConfig.FILE_MANAGEMENT_MODAL_LIST_VIEW,
              icon: <List size={18} />,
            },
            {
              value: "grid",
              label: textConfig.FILE_MANAGEMENT_MODAL_GRID_VIEW,
              icon: <Grid size={18} />,
            },
          ]}
        />

        {/* Búsqueda de archivos comentada - el endpoint muestra error 400 "Prefix must end with '/'" */}
        {/*
        <div className="mb-4 space-y-3">
          <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(); }} className="space-y-3">
            <div>
              <label
                htmlFor="file-search"
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {textConfig.FILE_MANAGEMENT_MODAL_SEARCH_LABEL || "Buscar por archivo"}
              </label>
              <div className="relative">
                <input
                  id="file-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                                     placeholder={textConfig.FILE_MANAGEMENT_MODAL_SEARCH_PLACEHOLDER || "Ingrese el nombre de la colección o archivo..."}
                  className={`w-full px-3 py-2 pl-10 rounded-md border-2 border-[#1BCF66] transition-colors ${
                    isDarkMode
                      ? "bg-[#2D2D2D] text-white placeholder-gray-400 focus:border-[#1BCF66] focus:ring-1 focus:ring-[#1BCF66]"
                      : "bg-white text-gray-900 placeholder-gray-500 focus:border-[#1BCF66] focus:ring-1 focus:ring-[#1BCF66]"
                  }`}
                  disabled={isSearching}
                />
                <Search
                  size={16}
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={!searchQuery.trim() || isSearching}
                className={`px-4 py-2 rounded-md transition-colors ${
                  searchQuery.trim() && !isSearching
                    ? "bg-[#1BCF66] text-white hover:bg-[#1BCF66]/90"
                    : "bg-gray-400 text-gray-600 cursor-not-allowed"
                }`}
              >
                {textConfig.FILE_MANAGEMENT_MODAL_SEARCH_BUTTON || "Buscar"}
              </button>
              {searchQuery && (
                                     <button
                       type="button"
                       onClick={handleClearSearchSubmit}
                       disabled={isSearching}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm transition-colors ${
                    isDarkMode
                      ? "text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500"
                      : "text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <X size={14} />
                  {textConfig.FILE_MANAGEMENT_MODAL_CLEAR_BUTTON || "Limpiar"}
                </button>
              )}
                         </div>
           </form>
        */}

        <InfoBox $isDark={isDarkMode}>
          <Info className="info-icon" size={34} />
          <Typography className="info-text">
            Los archivos y carpetas se actualizan automáticamente después de agregar o eliminar elementos. Si no ves los cambios inmediatamente, puedes usar el botón de actualizar.
          </Typography>
        </InfoBox>

        <FileNavigation
          currentPath={currentPath}
          isDarkMode={isDarkMode}
          isRefreshing={isRefreshing}
          onNavigateRoot={handleNavigateToRoot}
          onNavigateBack={handleNavigateBack}
          onNavigateTo={(index) => handleNavigateToPath(currentPath.slice(0, index + 1))}
          onManualRefresh={() => handleManualRefresh(setAlertConfig)}
          truncateFileName={truncateFileName}
        />

        <FileExplorer
          collections={collections}
          currentPath={currentPath}
          isLoading={isLoading}
          isDarkMode={isDarkMode}
          viewMode={viewMode}
          onFolderClick={handleFolderClick}
          onDeleteFile={(fileName) =>
            handleDeleteFile(fileName, setAlertConfig)
          }
          truncateFileName={truncateFileName}
          onDownloadFile={handleDownloadFile}
        />

        {pagination.totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              isLoading={isLoading}
            />
          </Box>
        )}

        {currentPath.length === 0 && (
          <CollectionCreator
            newCollectionName={newCollectionName}
            setNewCollectionName={setNewCollectionName}
            newCollectionFiles={newCollectionFiles}
            setNewCollectionFiles={setNewCollectionFiles}
            collectionNameError={collectionNameError}
            setCollectionNameError={setCollectionNameError}
            fileNameError={fileNameError}
            setFileNameError={setFileNameError}
            showInlineSuccessMessage={showInlineSuccessMessage}
            isDarkMode={isDarkMode}
            isCreatingCollection={isCreatingCollection}
            onCreateCollection={() => handleCreateCollection(setAlertConfig)}
            onNewCollectionFileSelection={(files) => {
              if (files) {
                const fileArray = Array.from(files);
                processFileSelection(fileArray, setNewCollectionFiles, true);
              }
            }}
            setAlertConfig={setAlertConfig}
            truncateFileName={truncateFileName}
          />
        )}

        <FolderCreator
          collections={collections}
          currentPath={currentPath}
          newCollectionName={newCollectionName}
          setNewCollectionName={setNewCollectionName}
          collectionNameError={collectionNameError}
          isDarkMode={isDarkMode}
          disabled={isCreatingCollection}
          setCollections={setCollections}
        />

        <FileUploadSection
          currentPath={currentPath}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          fileNameError={fileNameError}
          setFileNameError={setFileNameError}
          showFileUploadSuccessMessage={showFileUploadSuccessMessage}
          isDarkMode={isDarkMode}
          isUploading={isUploading}
          textConfig={textConfig}
          onUploadFiles={() => handleUploadFiles(setAlertConfig)}
          onFileSelection={(files) => {
            if (files) {
              const fileArray = Array.from(files);
              processFileSelection(fileArray, setSelectedFiles);
            }
          }}
          setAlertConfig={setAlertConfig}
          truncateFileName={truncateFileName}
        />

        <Box display="flex" mt={3} justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              backgroundColor: "#FF3D3D",
              "&:hover": {
                backgroundColor: "#FF5252",
              },
            }}
          >
            {textConfig.FILE_MANAGEMENT_MODAL_CLOSE_BUTTON}
          </Button>
        </Box>
      </BaseModal>

      <CustomAlert
        open={alertConfig.open}
        type={alertConfig.type}
        message={alertConfig.message}
        title={alertConfig.title}
        onClose={handleCloseAlert}
        onConfirm={async () => {
          if (alertConfig.onConfirm) {
            await alertConfig.onConfirm();
          }
        }}
        textConfig={textConfig}
      />
    </>
  );
}